import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [blogOpen, setBlogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [mobileBlogOpen, setMobileBlogOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const blogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
      if (blogRef.current && !blogRef.current.contains(e.target as Node)) {
        setBlogOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const articleLinks = [
    { name: 'Complete Guide', path: '/superbuy-spreadsheet-complete-guide', icon: 'ri-book-open-line' },
    { name: "Beginner's Guide", path: '/superbuy-spreadsheet-beginners', icon: 'ri-user-star-line' },
    { name: 'Safety Guide', path: '/superbuy-spreadsheet-safety', icon: 'ri-shield-check-line' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-foreground-950 no-underline">
            <span className="text-xl font-heading font-bold whitespace-nowrap">Superbuy Spread</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Home
            </Link>
            <Link to="/#categories" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Spreadsheet
            </Link>
            <div ref={categoryRef} className="relative">
              <button
                onClick={() => { setCategoryOpen(!categoryOpen); setBlogOpen(false); }}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {categoryOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-lg z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div ref={blogRef} className="relative">
              <button
                onClick={() => { setBlogOpen(!blogOpen); setCategoryOpen(false); }}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Guides
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${blogOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {blogOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-lg z-50">
                  {articleLinks.map((article) => (
                    <Link
                      key={article.path}
                      to={article.path}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      <i className={`${article.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                      {article.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/#about" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              About
            </Link>
            <Link to="/#faq" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              FAQ
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold border border-foreground-300 rounded-full text-foreground-800 hover:bg-foreground-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-external-link-line text-sm w-4 h-4 flex items-center justify-center"></i>
              Superbuy Sheet
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 text-foreground-700 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu - Enhanced */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-1">
            {/* Primary nav links */}
            <Link to="/" className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-950 rounded-xl hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              Home
            </Link>
            <Link to="/#categories" className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-950 rounded-xl hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
              <i className="ri-grid-line text-lg w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              Spreadsheet
            </Link>

            {/* Categories - Expandable on mobile */}
            <div>
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-950 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-folder-open-line text-lg w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-base transition-transform ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCategoryOpen && (
                <div className="ml-3 mt-1 pl-5 border-l-2 border-background-200/70">
                  <div className="grid grid-cols-1 gap-0.5 py-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-background-100 hover:text-foreground-950 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center text-foreground-400`}></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Guides - Expandable on mobile */}
            <div>
              <button
                onClick={() => setMobileBlogOpen(!mobileBlogOpen)}
                className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-950 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-article-line text-lg w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                  Guides
                </span>
                <i className={`ri-arrow-down-s-line text-base transition-transform ${mobileBlogOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileBlogOpen && (
                <div className="ml-3 mt-1 pl-5 border-l-2 border-background-200/70">
                  <div className="flex flex-col gap-0.5 py-1">
                    {articleLinks.map((article) => (
                      <Link
                        key={article.path}
                        to={article.path}
                        className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-background-100 hover:text-foreground-950 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <i className={`${article.icon} text-base w-5 h-5 flex items-center justify-center text-foreground-400`}></i>
                        {article.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/#about" className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-950 rounded-xl hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
              <i className="ri-information-line text-lg w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              About
            </Link>
            <Link to="/#faq" className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-950 rounded-xl hover:bg-background-100 transition-colors" onClick={() => setMobileMenuOpen(false)}>
              <i className="ri-question-answer-line text-lg w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              FAQ
            </Link>

            {/* CTA */}
            <div className="mt-4 pt-4 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 text-sm font-semibold bg-foreground-950 text-background-50 rounded-full whitespace-nowrap"
              >
                <i className="ri-external-link-line text-sm w-4 h-4 flex items-center justify-center"></i>
                Superbuy Sheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}